import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import { createPortal } from 'react-dom';

const DashboardCard = ({ 
  Icon, 
  title, 
  description, 
  onClick, 
  isMaintenanceCard = false, 
  isSelected = false,
  disabled = false
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  
  const handleMouseMove = (e) => {
    setTooltipPosition({
      x: e.clientX,
      y: e.clientY
    });
  };
  
  const handleClick = (e) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    onClick();
  };
  
  // Create a tooltip component that uses React Portal
  const Tooltip = () => {
    if (!isHovered || disabled) return null;
    
    return createPortal(
      <div
        className="fixed z-[9999] max-w-xs px-3 py-2 rounded-md bg-gray-900 text-white text-sm 
               transition-opacity duration-200 opacity-90 whitespace-pre-wrap break-words pointer-events-none"
        style={{
          left: `${tooltipPosition.x}px`,
          top: `${tooltipPosition.y + 20}px`,
          transform: 'translateX(-50%)'
        }}
      >
        {description}
      </div>,
      document.body
    );
  };
  
  return (
    <>
      <motion.div
        whileHover={{ scale: 1.02 }}
        animate={{ scale: isSelected ? 1.02 : 1 }}
        whileTap={{ scale: 0.98 }}
        className={`
          group rounded-lg flex items-center gap-4 cursor-pointer 
          transition-all h-[65px] w-full relative
          ${isSelected 
            ? 'bg-blue-800 text-white shadow-md' 
            : `bg-gray-100 text-blue-800 hover:bg-blue-700 hover:text-white hover:shadow-sm`
          }
          ${disabled ? 'bg-gray-400 text-gray-600 cursor-not-allowed opacity-60 hover:bg-gray-400 hover:text-gray-600' : ''}
        `}
        onClick={handleClick}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Icon - Color changes on hover */}
        <div className={`w-8 h-8 flex-shrink-0 ${
          isSelected 
            ? 'text-white' 
            : 'text-blue-800 group-hover:text-white'
        } ml-4 mr-2`}>
          <Icon className="w-full h-full" />
        </div>
        
        {/* Vertical Line - Color changes on hover */}
        <div 
          className={`w-[2px] h-[65%] absolute left-[4.5rem] top-1/2 -translate-y-1/2 ${
            isSelected 
              ? 'bg-white' 
              : 'bg-red-500 group-hover:bg-white'
          }`}
        />
        
        {/* Content */}
        <div className="flex-1 overflow-hidden pl-4">
          <h3 
            className={`font-semibold mb-0 truncate text-sm ${
              isSelected 
                ? 'text-white' 
                : 'text-blue-800 group-hover:text-white'
            }`}
          >
            {title}
          </h3>
        </div>
        
        {/* Chevron Icon - Color changes on hover */}
        <div className={`
          absolute ${isMaintenanceCard ? 'left-0' : 'right-0'} 
          top-1/2 -translate-y-1/2 
          transition-all duration-200
          ${isMaintenanceCard ? '-translate-x-4' : 'translate-x-4'}
          ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
        `}>
          {isMaintenanceCard ? (
            <ChevronLeft className={`w-5 h-5 ${
              isSelected 
                ? 'text-white' 
                : 'text-blue-800 group-hover:text-white'
            }`} />
          ) : (
            <ChevronRight className={`w-5 h-5 ${
              isSelected 
                ? 'text-white' 
                : 'text-blue-800 group-hover:text-white'
            }`} />
          )}
        </div>
      </motion.div>
      
      {/* Tooltip Portal */}
      <Tooltip />
    </>
  );
};

export default DashboardCard;